import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '@shared/auth/AuthContext';
import { AuthRole, roleManager } from './roleManager';
import { logger } from '@shared/logging/logger';

interface RBACContextType {
  role: AuthRole;
  permissions: Set<string>;
  hasRole: (roles: AuthRole | AuthRole[]) => boolean;
  isManager: boolean;
}

const RBACContext = createContext<RBACContextType | undefined>(undefined);

export const RBACProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { role: authRole, isLoading: authLoading } = useAuth();
  const [role, setRole] = useState<AuthRole>('unknown');
  const [permissions, setPermissions] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (authLoading) return;

    const currentRole = (authRole as AuthRole) || 'unknown';
    setRole(currentRole);
    
    const calculatedPerms = roleManager.getPermissionsForRole(currentRole);
    setPermissions(calculatedPerms);

    logger.debug('security', `RBAC Context updated for role: ${currentRole} with ${calculatedPerms.size} permission vectors.`);
  }, [authRole, authLoading]);

  const hasRole = React.useCallback((roles: AuthRole | AuthRole[]): boolean => {
    if (Array.isArray(roles)) {
      return roles.includes(role);
    }
    return role === roles;
  }, [role]);

  const isManager = role === 'manager';

  const value = React.useMemo(() => ({
    role,
    permissions,
    hasRole,
    isManager
  }), [role, permissions, hasRole, isManager]);

  return (
    <RBACContext.Provider value={value}>
      {children}
    </RBACContext.Provider>
  );
};

export const useRole = () => {
  const context = useContext(RBACContext);
  if (context === undefined) {
    throw new Error('useRole must be used within an RBACProvider');
  }
  return context;
};
